import type { ReactNode } from "react";

/**
 * Primitivas visuales compartidas del backoffice: la etiqueta de categoría
 * (recuadro naranja), el título de página (con fade naranja) y los skeletons de
 * carga. Todas pintan con los tokens del sistema —el naranja es `--fc-color-accent`,
 * el del logo—, no con colores sueltos.
 */

/**
 * Etiqueta de categoría: la palabra de área (Compras, Inventario, Configuración…)
 * en un recuadro naranja, para que se distinga de un vistazo del resto del texto.
 */
export function EtiquetaCategoria({
  children,
  size = "sm",
  className = "",
}: {
  children: ReactNode;
  /** "sm": eyebrow diminuto. "md": encabezado de sección, algo mayor. */
  size?: "sm" | "md";
  className?: string;
}) {
  const tam = size === "md" ? "text-xs" : "text-2xs";
  // Texto naranja, sin recuadro: el color distingue la categoría sin la carga
  // visual de un badge en cada sección.
  return (
    <span
      className={`inline-block w-fit self-start font-data font-semibold uppercase tracking-[0.16em] ${tam} ${className}`.trim()}
      style={{ color: "var(--fc-color-accent)" }}
    >
      {children}
    </span>
  );
}

export type EstadoRequisito = "ok" | "falta" | "cargando";

export interface Requisito {
  label: string;
  estado: EstadoRequisito;
  /** Aclaración corta (p. ej. cuántas cuentas hay, o qué falta). */
  detalle?: string;
}

/**
 * Panel de preparación (readiness). Muestra, requisito a requisito, qué está listo
 * (verde ✓), qué falta (rojo ✗) y qué se está comprobando (neutro …), con un resumen
 * arriba. Hace el estado HONESTO: si algo bloquea operar, se ve —no se pinta verde lo
 * que no está—. Reutilizable para el arranque de la organización, la contabilidad, la
 * caja, etc.
 */
export function PanelRequisitos({
  titulo,
  requisitos,
  bloqueante,
}: {
  titulo: string;
  requisitos: Requisito[];
  /** Por qué importa: se muestra en ámbar cuando falta algo. */
  bloqueante?: string;
}) {
  const faltan = requisitos.filter((r) => r.estado === "falta").length;
  const cargando = requisitos.some((r) => r.estado === "cargando");
  const listo = !cargando && faltan === 0;
  const color = listo
    ? "var(--fc-color-success)"
    : cargando
      ? "var(--fc-color-text-muted)"
      : "var(--fc-color-warning)";
  return (
    <section
      className="flex flex-col gap-2 rounded-panel border border-border bg-surface p-4"
      style={{ borderLeftWidth: "3px", borderLeftColor: color }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-brand text-sm font-semibold">{titulo}</span>
        <span className="font-data text-2xs font-semibold" style={{ color }}>
          {cargando ? "● comprobando…" : listo ? "● listo" : `● faltan ${faltan}`}
        </span>
      </div>
      <ul className="flex flex-col gap-1">
        {requisitos.map((r, i) => {
          const c =
            r.estado === "ok"
              ? "var(--fc-color-success)"
              : r.estado === "falta"
                ? "var(--fc-color-danger)"
                : "var(--fc-color-text-muted)";
          const simbolo = r.estado === "ok" ? "✓" : r.estado === "falta" ? "✗" : "…";
          return (
            <li key={i} className="flex items-baseline gap-2 text-sm">
              <span className="font-data font-bold" style={{ color: c }}>
                {simbolo}
              </span>
              <span
                className={r.estado === "falta" ? "font-medium" : ""}
                style={r.estado === "falta" ? { color: "var(--fc-color-danger)" } : undefined}
              >
                {r.label}
              </span>
              {r.detalle ? <span className="text-2xs text-text-muted">— {r.detalle}</span> : null}
            </li>
          );
        })}
      </ul>
      {!listo && !cargando && bloqueante ? (
        <p className="text-2xs" style={{ color: "var(--fc-color-warning)" }}>
          {bloqueante}
        </p>
      ) : null}
    </section>
  );
}

/** Título de página (h1) con el fade naranja debajo. */
export function TituloPagina({ children }: { children: ReactNode }) {
  return (
    <h1 className="fc-titulo-fade font-brand text-3xl font-bold tracking-tight text-text">
      {children}
    </h1>
  );
}

/** Un bloque que reluce mientras carga. */
export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`fc-skeleton ${className}`.trim()} aria-hidden="true" />;
}

/**
 * Skeleton con forma de tabla, para el estado de carga de las pantallas de lista.
 * Imita la cabecera y unas cuantas filas, así el salto a los datos reales no
 * descoloca la página.
 */
export function SkeletonTabla({ filas = 6 }: { filas?: number }) {
  return (
    <div
      className="overflow-hidden rounded-panel border border-border bg-surface"
      role="status"
      aria-label="Cargando…"
    >
      <div className="border-b border-border px-3 py-3">
        <Skeleton className="h-4 w-40" />
      </div>
      {Array.from({ length: filas }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 border-b border-border/50 px-3 py-3.5 last:border-0"
        >
          <Skeleton className="h-3.5 w-1/4" />
          <Skeleton className="h-3.5 w-1/3" />
          <Skeleton className="h-3.5 w-1/6" />
          <Skeleton className="ml-auto h-3.5 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton para paneles/tarjetas: unas líneas de texto simuladas. */
export function SkeletonLineas({ lineas = 3 }: { lineas?: number }) {
  return (
    <div className="flex flex-col gap-2" role="status" aria-label="Cargando…">
      {Array.from({ length: lineas }).map((_, i) => (
        <Skeleton key={i} className={`h-3.5 ${i === lineas - 1 ? "w-1/2" : "w-full"}`} />
      ))}
    </div>
  );
}
